import "./SchedulePage.scss";
import Form from "../../components/Form/Form";
import { useEffect, useState } from "react";
import Queue from "../../components/Queue/Queue";
import axios from "axios";
import { BASE_URL } from "../../api/api";
import { v4 as uuidv4 } from "uuid";
import {
  formatDateTimeToUnix,
  getURLParams,
  validateSubreddit,
} from "../../utils/helper";
import Spin from "../../components/Spin/Spin";
import { useHistory } from "react-router-dom";

const SchedulePage = ({
  userData,
  setUserData,
  recommendedTime,
  setAnalysis,
}) => {
  const [formErrors, setFormErrors] = useState({
    title: "",
    content: "",
    datetime: false,
    subreddit: false,
    limit: false,
  });

  const [editErrors, setEditErrors] = useState({
    title: false,
    content: false,
    datetime: false,
    subreddit: false,
    limit: false,
  });

  const history = useHistory();

  const addPost = async (newPost, userData) => {
    const instance = axios.create({ withCredentials: true });
    instance.post(
      `${BASE_URL}/user/post`,
      {
        post: newPost,
      },
      {
        headers: {
          Authorization: `Bearer ${userData.access}`,
        },
      }
    );
  };

  const formSubmitHandler = async (e, time, setSelectedItem) => {
    e.preventDefault();

    if (userData.scheduled.length === 5) {
      setFormErrors({ ...formErrors, limit: true });
      return;
    }

    // form input validation
    const subredditExists =
      e.target.subreddit.value &&
      (await validateSubreddit(e.target.subreddit.value));

    setFormErrors({
      datetime: !e.target.date.value ? true : false,
      subreddit: !subredditExists ? true : false,
      title: !e.target.title.value ? "A valid field entry is required*" : "",
      content: !e.target.content.value
        ? "A valid field entry is required*"
        : "",
    });

    if (
      !e.target.title.value ||
      !e.target.content.value ||
      !subredditExists ||
      !e.target.date.value
    ) {
      // console.log("error");
      return;
    }

    const newPost = {
      datetime: formatDateTimeToUnix(e.target.date.value, time),
      subreddit: e.target.subreddit.value,
      title: e.target.title.value,
      content: e.target.content.value,
      id: uuidv4(),
      createdOn: Date.now(),
    };

    // reset
    e.target.reset();
    setSelectedItem({
      datetime: "",
      subreddit: "",
      title: "",
      content: "",
    });
    window.history.replaceState("", "", "/schedule");

    //  send new data to the backend
    try {
      await addPost(newPost, userData);
      setUserData({
        ...userData,
        scheduled: [...userData.scheduled, newPost],
      });
    } catch (err) {
      if (err.response.status === 403) {
        // if access token has expired, GET new access token
        try {
          const token = await axios.get(`${BASE_URL}/auth/refresh`, {
            withCredentials: true,
          });
          setUserData({ ...userData, access: token });
          await addPost(newPost, userData);
          setUserData({
            ...userData,
            scheduled: [...userData.scheduled, newPost],
          });
        } catch (err) {
          console.log("POST new post -> GET new access token error", err);
        }
      }
      console.log("POST new post error", err);
    }
  };

  const deleteHandler = async (id) => {
    try {
      const instance = axios.create({ withCredentials: true });
      await instance.delete(`${BASE_URL}/user/post`, {
        data: {
          postId: id,
        },
        headers: {
          Authorization: `Bearer ${userData.access}`,
        },
      });

      const data = {
        ...userData,
        scheduled: userData.scheduled.filter((user) => user.id !== id),
      };
      setUserData(data);
    } catch (err) {
      console.log("DELETE Queue-item error", err);
      if (err.response.status === 403) {
        // GET new access token
        try {
          const token = await axios.get(`${BASE_URL}/auth/refresh`, {
            withCredentials: true,
          });
          setUserData({ ...userData, access: token });
          const instance = axios.create({ withCredentials: true });
          await instance.delete(`${BASE_URL}/user/post`, {
            data: {
              postId: id,
            },
            headers: {
              Authorization: `Bearer ${userData.access}`,
            },
          });
        } catch (err) {
          console.log("DELETE post -> GET new access token error", err);
        }
      }
    }
  };

  const editHandler = async (e, id, editedObj, setOpenEditModal) => {
    e.preventDefault();

    // form input validation
    const subredditExists =
      e.target.subreddit.value &&
      (await validateSubreddit(e.target.subreddit.value));

    setEditErrors({
      datetime: !e.target.date.value ? true : false,
      subreddit: !subredditExists ? true : false,
      title: !e.target.title.value ? true : false,
      content: !e.target.content.value ? true : false,
    });

    if (
      !e.target.title.value ||
      !e.target.content.value ||
      !subredditExists ||
      !e.target.date.value
    ) {
      // console.log("error");
      return;
    }

    try {
      const instance = axios.create({ withCredentials: true });
      await instance.put(
        `${BASE_URL}/user/post`,
        {
          editedPost: editedObj,
        },
        {
          headers: {
            Authorization: `Bearer ${userData.access}`,
          },
        }
      );
      setUserData({
        ...userData,
        scheduled: userData.scheduled.map((post) => {
          if (post.id === id) {
            return { ...post, ...editedObj };
          } else {
            return post;
          }
        }),
      });
      setOpenEditModal(false);
    } catch (err) {
      if (err.response.status === 403) {
        // GET new access token
        try {
          const instance = axios.create({ withCredentials: true });
          const token = await instance.get(`${BASE_URL}/auth/refresh`);
          setUserData({ ...userData, access: token });

          instance.put(
            `${BASE_URL}/user/post`,
            {
              editedPost: editedObj,
            },
            {
              headers: {
                Authorization: `Bearer ${userData.access}`,
              },
            }
          );
        } catch (err) {
          console.log("PUT post -> GET new access token error", err);
        }
      }
    }
  };

  useEffect(() => {
    document.title = `Eureka | Scheduling`;
    setAnalysis(true);

    if (window.location.search.includes("code" && "state")) {
      const { code } = getURLParams(window.location.search);

      const getTokens = async (code) => {
        try {
          const instance = axios.create({ withCredentials: true });
          const res = await instance.post(`${BASE_URL}/reddit/login-callback`, {
            code: code,
          });
          setUserData({ ...res.data.userData });
          window.sessionStorage.setItem("_s", res.data._s);
        } catch (err) {
          // if user declines Reddit log in we land here
          // console.log("getTokens error", err);
        }
      };
      getTokens(code);
      window.history.replaceState(null, "", "/schedule");
    } else if (!window.sessionStorage.getItem("_s")) {
      history.push("/");
    }
  }, [history, setAnalysis, setUserData]); // causes 3 renders setAnalysis, userData, setUserData

  return userData.username === null ? (
    <Spin modifier={"schedule"} />
  ) : (
    <section className="schedule">
      <Form
        formSubmitHandler={formSubmitHandler}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        recommendedTime={recommendedTime}
      />
      <Queue
        queueData={userData.scheduled}
        deleteHandler={deleteHandler}
        editHandler={editHandler}
        editErrors={editErrors}
      />
    </section>
  );
};

export default SchedulePage;
